/**
 * An entity's financial year, as months.
 *
 * The Add Entity dialog and the Entity master ask for a start month and an end month; the API stores two
 * `DATE` columns and derives them from the months server-side (`FinancialYears` in the organization module).
 * This module is the client half of that contract, shared because both features write the same two columns:
 *
 * - a financial year spans exactly 12 months, so the end month is the month **before** the start month,
 *   wrapping (April → March, January → December);
 * - an edit pre-selects the months the stored dates fall in;
 * - the mocks derive dates with the same rule the server uses, so mocked and real screens agree.
 *
 * Months are 1-12 throughout, as the API takes them -- not JavaScript's 0-11.
 */

export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/** The month that closes a financial year opening in `startMonth`. */
export function financialYearEndMonthFor(startMonth: number): number {
  return startMonth === 1 ? 12 : startMonth - 1;
}

/** `'April'` for 4; empty for anything outside 1-12. */
export function monthName(month: number | null | undefined): string {
  if (month === null || month === undefined || !Number.isInteger(month)) return '';
  return MONTH_NAMES[month - 1] ?? '';
}

/** 4 for `'April'`, or null when the label names no month. */
export function monthFromName(name: string | null | undefined): number | null {
  const index = MONTH_NAMES.findIndex((label) => label === name);
  return index === -1 ? null : index + 1;
}

/**
 * The month a stored date falls in, 1-12, or null.
 *
 * Read off the `yyyy-MM` prefix rather than through `Date`: the API answers `"2026-04-01"`, and parsing that
 * would make it UTC midnight -- 31 March west of Greenwich. Any other shape falls back to the local day.
 */
export function monthOfDate(value: string | null | undefined): number | null {
  if (!value) return null;
  const prefix = /^(\d{4})-(\d{2})/.exec(value);
  if (prefix && !value.includes('T')) {
    const month = Number(prefix[2]);
    return month >= 1 && month <= 12 ? month : null;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getMonth() + 1;
}

function isoDay(year: number, month: number, day: number): string {
  return `${String(year)}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * The stored dates for a start month, by the server's rule -- used by the mocks only.
 *
 * Start = the 1st of the start month in the financial year that contains `today`; end = the day before the
 * same date a year later, i.e. the last day of the end month.
 */
export function financialYearDates(
  startMonth: number,
  today: Date = new Date(),
): { start: string; end: string } {
  const year = today.getMonth() + 1 >= startMonth ? today.getFullYear() : today.getFullYear() - 1;
  const endMonth = financialYearEndMonthFor(startMonth);
  const endYear = endMonth < startMonth ? year + 1 : year;
  /* Day 0 of the following month is the last day of this one. */
  const lastDay = new Date(endYear, endMonth, 0).getDate();
  return { start: isoDay(year, startMonth, 1), end: isoDay(endYear, endMonth, lastDay) };
}
